import { z } from 'zod';

// ---------------------------------------------------------------------------
// Action type union
// ---------------------------------------------------------------------------

export type ActionType = 'merge' | 'keyword_filter' | 'ai_filter' | 'summarize' | 'translate';

// ---------------------------------------------------------------------------
// Action config schemas
// ---------------------------------------------------------------------------

export const MergeConfigSchema = z.object({
  type: z.literal('merge'),
});

export const KeywordFilterConfigSchema = z.object({
  type: z.literal('keyword_filter'),
  mode: z.enum(['include', 'exclude']),
  keywords: z.array(z.string().min(1)).min(1).max(20),
});

export const AIFilterConfigSchema = z.object({
  type: z.literal('ai_filter'),
  description: z.string().min(10),
  threshold: z.number().min(0.5).max(0.9).default(0.7),
});

export const SummarizeConfigSchema = z.object({
  type: z.literal('summarize'),
  length: z.enum(['short', 'medium']).default('short'),
  include_read_time: z.boolean().default(true),
  output: z.enum(['prepend_to_content']).default('prepend_to_content'),
});

export const TranslateConfigSchema = z.object({
  type: z.literal('translate'),
  target_language: z.enum(['en', 'zh-CN']),
  source: z.enum(['summary', 'content']).default('summary'),
  display: z.enum(['bilingual', 'translated_only']).default('bilingual'),
});

export const ActionConfigSchema = z.discriminatedUnion('type', [
  MergeConfigSchema,
  KeywordFilterConfigSchema,
  AIFilterConfigSchema,
  SummarizeConfigSchema,
  TranslateConfigSchema,
]);

export const FeedActionsSchema = z.array(ActionConfigSchema).superRefine((actions, ctx) => {
  let hasPreviousSummarize = false;

  actions.forEach((action, index) => {
    if (action.type === 'translate' && action.source === 'summary' && !hasPreviousSummarize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Translate source summary requires a preceding Summarize action',
        path: [index, 'source'],
      });
    }

    if (action.type === 'summarize') {
      hasPreviousSummarize = true;
    }
  });
});

export type MergeConfig = z.infer<typeof MergeConfigSchema>;
export type KeywordFilterConfig = z.infer<typeof KeywordFilterConfigSchema>;
export type AIFilterConfig = z.infer<typeof AIFilterConfigSchema>;
export type SummarizeConfig = z.infer<typeof SummarizeConfigSchema>;
export type TranslateConfig = z.infer<typeof TranslateConfigSchema>;
export type ActionConfig = z.infer<typeof ActionConfigSchema>;

// ---------------------------------------------------------------------------
// Feed schemas
// ---------------------------------------------------------------------------

export const feedSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  public_token: z.string().min(16),
  name: z.string().min(1),
  is_enabled: z.boolean(),
  actions: FeedActionsSchema.default([]),
  last_synced_at: z.string().datetime().nullable(),
  last_sync_trace_id: z.string().nullable().default(null),
  created_at: z.string().datetime(),
});

export type Feed = z.infer<typeof feedSchema>;

export const createFeedSchema = z.object({
  name: z.string().min(1),
  is_enabled: z.boolean().optional().default(true),
  actions: FeedActionsSchema.optional().default([]),
});

export type CreateFeed = z.infer<typeof createFeedSchema>;

export const updateFeedSchema = z.object({
  name: z.string().min(1).optional(),
  is_enabled: z.boolean().optional(),
  actions: FeedActionsSchema.optional(),
});

export type UpdateFeed = z.infer<typeof updateFeedSchema>;
